// =========================================================================
// SettingsDeveloperLayout
//
// Sub-layout for the consolidated "Developer" tab under Settings. Hosts
// the four developer-facing surfaces that were previously top-level
// tabs:
//
//   * Integrations  — outbound provider connections (Stripe, Twilio, …)
//   * Webhooks      — outbound HTTPS event endpoints
//   * API keys      — outbound read keys (`pck_live_*`)
//   * Connectors    — inbound integration keys (`pck_int_*`)
//
// Renders a second-level tab strip beneath the page header. Every tab
// here is admin-only; non-admins that somehow land on a child route
// see the page's own "Restricted" copy (each page already gates its
// own body on `useMyOrgRole`). Surfacing a non-admin gate at the
// layout level too would just double-render the restriction copy.
//
// URLs intentionally live under `/settings/developer/...` so the
// nav highlight reads from the URL. The legacy `/settings/<name>`
// paths still resolve (back-compat <Navigate replace> in App.tsx)
// so every docs anchor and email deep link keeps working.
// =========================================================================

import { NavLink, Outlet } from 'react-router-dom';
import { SmallCap } from '@pc/ui';

const SUB_TABS: Array<{ to: string; label: string }> = [
  { to: '/settings/developer/api-keys',     label: 'API keys' },
  { to: '/settings/developer/webhooks',     label: 'Webhooks' },
  { to: '/settings/developer/connectors',   label: 'Connectors' },
  { to: '/settings/developer/integrations', label: 'Integrations' },
];

export function SettingsDeveloperLayout() {
  return (
    <div>
      <header className="mb-4">
        <SmallCap>Developer</SmallCap>
        <p className="mt-1 text-sm text-muted max-w-2xl">
          Outbound API keys, signed webhook endpoints, inbound integration
          keys, and service provider connections — everything you need to
          script PeptideClients or wire it up to an outside system.
        </p>
      </header>

      {/* Sub-tab strip. Same edge-to-edge phone treatment as the parent
          SettingsLayout nav: `-mx-4 sm:mx-0` + `px-4 sm:px-0` so the
          underline rule bleeds to the screen edge on narrow viewports
          but stays padded on desktop. */}
      <nav className="flex items-center gap-1 border-b border-rule mb-6 overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 sm:flex-wrap">
        {SUB_TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            className={({ isActive }) =>
              `px-3 py-2 text-sm -mb-px border-b-2 whitespace-nowrap ${
                isActive ? 'text-ink border-ink' : 'text-muted border-transparent hover:text-ink'
              }`
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </div>
  );
}
